import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { FloatingFocusManager, FloatingList, FloatingPortal, autoUpdate, flip, offset, shift, useClick, useDismiss, useFloating, useInteractions, useListNavigation, useMergeRefs, useRole, useTransitionStyles, useTypeahead, } from "@floating-ui/react";
import { cloneElement, createContext, forwardRef, isValidElement, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { MenuPopper, menuPopperClasses } from "./MenuPopper";
import { mergeSx } from "../system";
// Internal hook that populates the MenuDropdown context
const useFloatingMenuDropdown = ({ placement = "bottom", offset: _offset = 5, open: initOpen, onOpenChange, }) => {
    const [open, setOpen] = useState(initOpen);
    const [activeIndex, setActiveIndex] = useState(null);
    const elementsRef = useRef([]);
    const labelsRef = useRef([]);
    useEffect(() => {
        setOpen(initOpen);
    }, [initOpen]);
    const { context, floatingStyles, refs } = useFloating({
        placement,
        open,
        onOpenChange: (value) => {
            setOpen(value);
            onOpenChange?.(value);
        },
        middleware: [offset(_offset), flip(), shift()],
        whileElementsMounted: autoUpdate,
    });
    const click = useClick(context);
    const dismiss = useDismiss(context, { bubbles: true });
    const role = useRole(context, {
        role: "menu",
    });
    // Handles keyboard and arrow navigation to satisfy the ARIA design pattern for menus
    // https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/
    // https://www.w3.org/WAI/ARIA/apg/patterns/menubar/
    const listNavigation = useListNavigation(context, {
        activeIndex,
        listRef: elementsRef,
        onNavigate: setActiveIndex,
        loop: true,
    });
    // Handles jumping to the first item that matches the typeahead input
    const typeahead = useTypeahead(context, {
        activeIndex,
        listRef: labelsRef,
        onMatch: setActiveIndex,
    });
    // isMounted replaces the open state provided by the context output of useFloating
    const { isMounted, styles: transitionStyles } = useTransitionStyles(context);
    const { getFloatingProps, getItemProps, getReferenceProps } = useInteractions([
        click,
        dismiss,
        role,
        listNavigation,
        typeahead,
    ]);
    return useMemo(() => ({
        open: isMounted,
        setOpen,
        activeIndex,
        elementsRef,
        labelsRef,
        floatingStyles,
        transitionStyles,
        context,
        refs,
        getFloatingProps,
        getItemProps,
        getReferenceProps,
    }), [
        isMounted,
        activeIndex,
        floatingStyles,
        transitionStyles,
        context,
        refs,
        getFloatingProps,
        getItemProps,
        getReferenceProps,
    ]);
};
const MenuDropdownContext = createContext(null);
export const MenuDropdown = ({ children, ...props }) => {
    const popover = useFloatingMenuDropdown(props);
    return _jsx(MenuDropdownContext.Provider, { value: popover, children: children });
};
// External hook that provides access to the internal useFloatingMenuDropdown hook via the MenuDropdownContext and protects against misuse
export const useMenuDropdown = () => {
    const context = useContext(MenuDropdownContext);
    return context ? context : undefined;
};
export const MenuTrigger = forwardRef(({ children, onClick, ...props }, forwardedRef) => {
    const menuContext = useMenuDropdown();
    const ref = useMergeRefs([menuContext?.refs.setReference, forwardedRef]);
    if (!menuContext) {
        return null;
    }
    return (children &&
        isValidElement(children) &&
        cloneElement(children, {
            ref: ref,
            "data-state": menuContext.open ? "open" : "closed",
            ...props,
            ...children.props,
            ...menuContext.getReferenceProps({
                onClick: (e) => {
                    e.stopPropagation();
                    menuContext.setOpen(!menuContext.open);
                    onClick?.(e);
                },
            }),
        }));
});
export const MenuPopperManager = forwardRef(({ className, sx, ...props }, propRef) => {
    const menuContext = useMenuDropdown();
    const ref = useMergeRefs([menuContext?.refs.setFloating, propRef]);
    if (!menuContext || !menuContext.open) {
        return null;
    }
    const { context, elementsRef, labelsRef, floatingStyles, transitionStyles, getFloatingProps, } = menuContext;
    return (_jsx(FloatingPortal, { children: _jsx(FloatingList, { elementsRef: elementsRef, labelsRef: labelsRef, children: _jsx(FloatingFocusManager, { context: context, children: _jsx(MenuPopper, { ref: ref, sx: mergeSx({
                        [`&.${menuPopperClasses.root}`]: {
                            ...floatingStyles,
                            ...transitionStyles,
                        },
                    }, sx), ...getFloatingProps(props) }) }) }) }));
});
/**
 * ## Definition
 * A menu is a component that displays a list of choices or actions in a popover.
 *
 * ## Behavior
 * - The popover is opened and closed by a trigger element that wraps any passed children.
 * - The popover can be closed via external clicks, clicking on the trigger, or pressing the escape key.
 * - When paired with the MenuItem component, the Menu component can be used to create an accessible dropdown menu that allow for keyboard navigation and typeahead functionality.
 * - Because accessibility is a key feature of this component, it can also be opened and closed via the keyboard by focusing on the node and clicking any of the `enter`, `down arrow` or `space` keys.
 *
 * ## Technical Limitations
 * - Because of the typeahead functionality, the menu component captures all key events. This means forms and text fields cannot be used as content within the menu. For this, a popper component should be used instead.
 * - The menu component is built with a portal and will not be influenced by other components in the flow as it is rendered at the end of the body element.
 */
export const Menu = forwardRef(({ content, placement, offset, open, onOpenChange, children, ...props }, propRef) => (_jsxs(MenuDropdown, { placement: placement, offset: offset, open: open, onOpenChange: onOpenChange, children: [_jsx(MenuTrigger, { children: children }), _jsx(MenuPopperManager, { ref: propRef, ...props, children: content })] })));
//# sourceMappingURL=Menu.js.map